<script setup>
import { onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { usePointsStore } from '@/stores/points'
import Highcharts from 'highcharts'
import Boost from 'highcharts/modules/boost'

Boost(Highcharts)

const props = defineProps({ 

    state: {
        type: Object,
        required: true
    },

    chartKey: {
        type: String,
        required: true
    },

    graphsCount: {
        type: Number,
        required: true
    },

    isShowResetZoomBtn: {
        type: Boolean,
        required: true
    }
})

const emit = defineEmits(['showResetZoomBtn', 'hideResetZoomBtn'])

const chart = ref(null)
const chartContainer = ref(null)
const debounce = ref(null)
const showGrid = ref(true)

const translaterStore = useTranslaterStore()
const pointsStore = usePointsStore()

const getTranslate = computed(() => translaterStore.getTranslate)
const carState      = computed(() => pointsStore.carState)

// Высота графиков в зависимости от количества и разрешения экрана
const responsiveRules = computed(() => {

    if(props.graphsCount === 1) {
        return [
            {
                condition: {
                    maxWidth: 1200
                },
                chartOptions: {
                    chart: {
                        height: 500,
                    }
                }
            },
            {
                condition: {
                    maxWidth: 900
                },
                chartOptions: {
                    chart: {
                        height: 450
                    }
                },
                
            },
            {
                condition: {
                    maxWidth: 767
                },
                chartOptions: {
                    chart: {
                        height: 400
                    }
                }
            },
            {
                condition: {
                    maxWidth: 585
                },
                chartOptions: {
                    chart: {
                        height: 300
                    }
                }
            }
        ]
    }

    return [
        {
            condition: {
                maxWidth: 1200
            },
            chartOptions: {
                chart: {
                    height: 300,
                }
            }
        },
        {
            condition: {
                maxWidth: 900
            },
            chartOptions: {
                chart: {
                    height: 250
                }
            },
            
        },
        {
            condition: {
                maxWidth: 767
            },
            chartOptions: {
                chart: {
                    height: 200
                }
            }
        }
    ]
})

/**
 * * Часовой пояс для построения графиков
 * * По умолчанию highcharts строит графики в UTC
 * @return { Number } -180 = GMT+0300
 */
const getGMTOffset = computed(() => {   

    if (props.state.length) {
        return props.state[0].date.getTimezoneOffset()
    }

    return -180
})

const options = computed(() => { 

    return {
        chart: {
            zoomType: 'x',
            panKey: "ctrl",
            panning: {
                enabled: true,
                type: 'x'
            },
            marginTop: 30,
            spacingBottom: 30,
            backgroundColor: '#F4F4F4',
            borderRadius: 16,
            height: 500,
            animation: false,
            resetZoomButton: {
                theme: {
                    style: {
                        display: 'none'

                    }
                }
            },
            events: {
                selection: selectionEvent
            }
        },
        time: {
            timezoneOffset: getGMTOffset.value
        },
        credits: {
            enabled: false
        },
        accessibility: {
            enabled: false
        },
        responsive: {
            rules: responsiveRules.value
        },
        title: {
            text: null
        },
        xAxis: {
            type: 'datetime',
            lineColor: '#C8C9C7',
            lineWidth: 1,
            dateTimeLabelFormats: {
                millisecond: '%S.%L',
                sec: '%M:%S',
                min: '%M:%S',
                hour: '%H:%M:%S',
                day: '%d.%m.%y'
            },
            minTickInterval: 100,
            minRange: 1000,
            crosshair: true,
            events: {
                setExtremes: syncExtremes
            }
        },
        yAxis: {
            title: {
                text: null
            },
            min: chartSeriesMin.value,
            lineColor: '#C8C9C7',
            lineWidth: 1,
            gridLineWidth: 1
        },
        legend: {
            enabled: false
        },
        plotOptions: {
            line: {
                cropThreshold: 300,
                color: '#1A57F3',
                marker: {
                    radius: 0
                },
                lineWidth: 1,
                states: {
                    hover: {
                        lineWidth: 1
                    }
                }
            },
            series: {
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            }
        },
        tooltip: {
            useHTML: true,
            formatter: function() {     

                let date = new Date(this.x).toLocaleDateString('ru-Ru')
                let time = new Date(this.x).toLocaleTimeString('ru-Ru')
                
                return `
                    <span class="uta-car-history-graph__tooltip">
                        <span class="uta-car-history-graph__tooltip-title">
                            ${this.y} ${this.series.name}
                        </span><br>
                        <span class="uta-car-history-graph__tooltip-subtitle">
                            ${date} ${time}
                        </span>
                    </span>
                `
        
            },
            positioner: function(labelWidth, labelHeight, point) {

                let rightEdge = this.chart.chartWidth - point.plotX

                let tooltipX = point.plotX - labelWidth / 4

                if (point.plotX < 50) {
                    tooltipX = point.plotX + 20
                }

                if (rightEdge < 100) {
                    tooltipX = point.plotX - 80
                }

                let tooltipY = point.plotY + labelHeight

                return {
                    x: tooltipX,
                    y: tooltipY
                }
            },
            borderColor: '#2C2A29',
            backgroundColor: '#2C2A29',
            borderRadius: 8,
            style: {
                color: '#fff',
                fontSize: '16px'
            }
        },

        series: [{
            type: 'line', // line | area
            step: 'left',
            name: chartUnit.value,
            data: seriesData.value
        }]
    }
})

const container = computed(() => 'ADTChartHistoryGraph-' + props.chartKey)
const seriesData = computed(() => props.state.map(item => [item.date.getTime(), item.value]))
const chartUnit = computed(() => getTranslate.value[carState.value[props.chartKey].unit ] || '')
const chartTitle = computed(() => {

    const title = getTranslate.value['STATE_' + props.chartKey.toUpperCase()] || props.chartKey.toUpperCase()

    const comma = chartUnit.value === '' ? '' : ','

    return `${title}${comma} ${chartUnit.value}`
})
const chartSeriesMin = computed(() => {

    let minValue = Math.floor(Math.min(...seriesData.value.map(el => el[1])))
    let maxValue = Math.ceil(Math.max(...seriesData.value.map(el => el[1])))

    if(minValue < 0) {

        return minValue * 1.4

    } else {

        return maxValue * -0.2
    }
})

// Выделение на графике
function selectionEvent() {

    emit('showResetZoomBtn')
}

// Синхронизация tooltips and crosshairs
function redefineHighcharts() {

    const eventsList = ['mousemove', 'touchmove', 'touchstart']

    eventsList.forEach((eventType) => {
        chartContainer.value.addEventListener(eventType, function (e) {

            let chart
            let point
            let event

            for (let i = 0; i < Highcharts.charts.length; i++) {
                chart = Highcharts.charts[i];
                
                event = chart.pointer.normalize(e);

                point = chart.series[0].searchPoint(event, true);

                if (point) {
                    point.highlight(e);
                }
            }
        }, {passive: true})
    })

    Highcharts.Pointer.prototype.reset = () => {
        return undefined
    }

    Highcharts.Point.prototype.highlight = function (event) {
        event = this.series.chart.pointer.normalize(event)
        this.onMouseOver()
        this.series.chart.tooltip.refresh(this)
        this.series.chart.xAxis[0].drawCrosshair(event, this)
    }
}

// Синхронизация зума
function syncExtremes(e) {

    let thisChart = this.chart

    if (e.trigger !== 'syncExtremes') {

        Highcharts.charts.forEach(function (chart) {
            if (chart !== thisChart) {
                if (chart.xAxis[0].setExtremes) {
                    chart.xAxis[0].setExtremes(
                        e.min,
                        e.max,
                        undefined,
                        false,
                        { trigger: 'syncExtremes' }
                    )
                }
            }
        })
    }
}

// Сброс зума графика
function resetZoom() {

    Highcharts.charts.map(el => el.xAxis[0].setExtremes(null, null))
    emit('hideResetZoomBtn')
}

// Инициазация Highcharts
function init() {

    chart.value = Highcharts.chart(container.value, options.value)

    // обновить размер графика
    chart.value.setSize(null)

    redefineHighcharts()

    window.addEventListener('resize', setChartWidth, {passive: true})
}

// Для установки ширины графика равной ширине контейнера-родителя 
// сначала сбрасываем ширину графика на 0 (0 считается false, так что ставим 1), 
// а затем ставим ширину графика по ширине контейнера-родителя. 
// Чтобы сппизить нагрузку на пересчёты не выполняем чаще чем 1 раз в 0.5 сек.
function setChartWidth() {
    if(!debounce.value) {
        debounce.value = setTimeout(() => {
            chart.value.update({
                chart: {
                    width: 1
                }
            })
            chart.value.update({
                chart: {
                    width: chartContainer.value.innerWidth
                }
            })
            debounce.value = null
        }, 500)
    }
}

onMounted(() => {

    if (seriesData.value.length) init()
})

onBeforeUnmount(() => {

    Highcharts.charts.splice(0, Highcharts.charts.length)
    window.removeEventListener('resize', setChartWidth, {passive: true})
})

watch(showGrid, (newVal) => {

    chart.value.update({
        yAxis: {
            gridLineWidth: newVal == true ? 1 : 0
        }
    })
})
</script>

<template>
    <div class="uta-car-history-graph">
        <div class="uta-car-history-graph__header">
            <h3 class="uta-car-history-graph__header-title">{{ chartTitle }}</h3>
            <div class="uta-car-history-graph__header-controls" v-if="seriesData.length !== 0">
                <input 
                    type="checkbox" 
                    :id="'ADTChartHistoryGraphGrid-' + chartKey" 
                    v-model="showGrid"
                >
                <label :for="'ADTChartHistoryGraphGrid-' + chartKey">{{ getTranslate.HISTORY_SHOW_GRID }}</label>
            </div>
        </div>
        <div class="uta-car-history-graph__body">
            <button v-if="isShowResetZoomBtn && seriesData.length !== 0" class="uta-car-history-graph__reset" @click.prevent="resetZoom">
                <img class="uta-car-history-graph__img" src="@/assets/images/icons/icon-zoom-out.png" alt="zoom-out">
            </button>

            <div v-if="seriesData.length !== 0" ref="chartContainer" :id="container"></div>

            <div 
                v-else
                class="uta-alert uta-alert--danger adt-states-history__message"
            >
                <span class="material-icons uta-alert__icon">error_outline</span>
                <span class="uta-alert__text">{{ getTranslate.HISTORY_EMPTY_VALUE }}</span>
            </div>
        </div>
    </div>    
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-history-graph.scss';
</style>